import {DetailsField, DetailsFieldsGrid} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {FemaleIcon, MaleIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {formatLocaleDate} from '@/dayjs'
import {PatientExtended} from '@/features/patients/types'
import {patientEducationToLabel, patientOccupationToLabel} from '@/features/patients/utils'
import {useTranslation} from '@/translations/i18n'
import {maritalStatusesToLabel} from '@/utilities/helpers'
import {useTheme} from 'styled-components'

export const PatientPersonalInfoDetails = ({patient}: {patient: PatientExtended}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()

    return (
        <DetailsFieldsGrid>
            <DetailsField>
                <Flexbox gap={1}>
                    <Text weight="500" size="textMd">
                        {patient.fullName}
                    </Text>
                    {patient.gender == 'male' && <MaleIcon size={18} color={palette.primary[600]} />}
                    {patient.gender == 'female' && <FemaleIcon size={18} color={palette.purple[600]} />}
                </Flexbox>
                <div>
                    <Text palette="neutral" shade="500" size="textSm">
                        {patient.birthCity
                            ? t('commons:birth_date_city', {
                                  date: formatLocaleDate(patient.birthDate, 'L'),
                                  city: `${patient.birthCity.name} (${patient.birthCity.provinceAcronym})`
                              })
                            : t('commons:birth_date_country', {
                                  date: formatLocaleDate(patient.birthDate, 'L'),
                                  country: patient.birthCountry.name
                              })}
                    </Text>
                </div>
            </DetailsField>

            <DetailsField>
                <Text palette="neutral" shade="500" size="textSm">
                    {t('commons:fiscal_code')}
                </Text>
                <Text weight="500" size="textMd">
                    {patient.taxCode}
                </Text>
            </DetailsField>

            <DetailsField>
                <Text palette="neutral" shade="500" size="textSm">
                    {t('commons:marital_status')}
                </Text>
                <Text weight="500" size="textMd">
                    {t(maritalStatusesToLabel[patient.maritalStatus])}
                </Text>
            </DetailsField>

            <DetailsField>
                <Text palette="neutral" shade="500" size="textSm">
                    {t('patients:occupation')}
                </Text>
                <Flexbox direction="column" gap={0.5}>
                    <Text weight="500" size="textMd">
                        {t(patientOccupationToLabel[patient.occupation])}
                    </Text>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t(patientEducationToLabel[patient.education])}
                    </Text>
                </Flexbox>
            </DetailsField>

            {patient.residence && (
                <DetailsField>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('commons:residence')}
                    </Text>
                    <Flexbox direction="column" gap={0.5}>
                        <Text weight="500" size="textMd">
                            {`${patient.residence.address} ${patient.residence.streetNumber}, ${patient.residence.city.name} (${patient.residence.city.provinceAcronym}) ${patient.residence.cap}`}
                        </Text>
                        {patient.residence.municipality && (
                            <Text palette="neutral" shade="500" size="textSm">
                                {patient.residence.municipality.name}
                            </Text>
                        )}
                        <Text palette="neutral" shade="500" size="textSm">
                            {patient?.residenceAslDistrict?.aslName ?? t('commons:out_region')}
                        </Text>
                    </Flexbox>
                </DetailsField>
            )}

            {patient.domicile && (
                <DetailsField>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('commons:domicile')}
                    </Text>
                    {patient.domicile.id == patient?.residence?.id ? (
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:same_as_residence')}
                        </Text>
                    ) : (
                        <>
                            <Text weight="500" size="textMd">
                                {`${patient.domicile.address} ${patient.domicile.streetNumber}, ${patient.domicile.city.name} (${patient.domicile.city.provinceAcronym}) ${patient.domicile.cap}`}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {patient?.domicileAslDistrict?.aslName ?? t('commons:out_region')}
                            </Text>
                        </>
                    )}
                </DetailsField>
            )}
        </DetailsFieldsGrid>
    )
}
